import styled from '@emotion/styled';
import React, { FC, memo, useMemo } from 'react';
import { PageButtonType, PageType } from '..';
import Icon from '~ComponentsImproved/Icon';
import Payments from '~Assets/icons/payments.svg';
import TicketIcon from '~Assets/icons/receipt_long.svg';
import Receipt from '~Assets/icons/receipt.svg';
import Print from '~Assets/icons/print.svg';
import KDS from '~Assets/icons/product_kitchen-display.svg';
import Layout from '~Assets/icons/edit_layout.svg';
import Other from '~Assets/icons/settings.svg';
import TablesIcon from '~Assets/icons/restaurant.svg';
import MapIcon from '~Assets/icons/map.svg';
import ReportIcon from '~Assets/icons/article.svg';
import StaffIcon from '~Assets/icons/person.svg';
import AppIcon from '~Assets/icons/phone_android.svg';
import ForwardArrow from '~Assets/icons/arrow_forward.svg';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useButton, useI18n } from '~Hooks';
import { Box } from '~Styles';

const PAGE_COLORS: Record<PageButtonType, string> = {
  printers: '#6fd6ff',
  layout: '#f69ed3',
  payments: '#93df93',
  receipts: '#ffb86c',
  KDS: '#f2d8a9',
  'kitchen-tickets': '#ada9f2',
  tables: '#a9d4f2',
  blueprints: '#3d7bff',
  other: '#c2c2c2',
  safT: '#f2a9f2',
  ejournal: '#f2a9f2',
  'staff-settings': 'var(--colorWhite)',
  app: '#6bbfc3',
};

const SettingsButton = styled.div<{ page: PageButtonType; touching?: boolean }>`
  background-color: var(--colorBlueGray1100);
  border-radius: 16px;
  padding: 8px;
  box-sizing: border-box;
  height: 148px;
  width: 148px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  transform: ${({ touching }) => (touching ? 'scale(0.96)' : '1')};
`;

const IconWrapper = styled.div`
  margin-bottom: 4px;
  flex-shrink: 0;
`;

/** Handheld row, mirrors HandySettingsBlock's SettingsBlockItem */
const Row = styled.div<{ touching?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--colorBlueGray1000);
  background: ${({ touching }) => (touching ? 'var(--colorBlueGray1000)' : 'transparent')};
  transition: background 0.1s ease-in-out;

  &:last-child {
    border-bottom: none;
  }
`;

/** Same 32px rounded square as Handy's RoundedIcon, tinted with the page colour */
const RoundedIcon = styled.div<{ bgColor: string }>`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  margin-right: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ bgColor }) => bgColor};
`;

const ICONS: Record<PageButtonType, string> = {
  printers: Print,
  layout: Layout,
  payments: Payments,
  receipts: Receipt,
  KDS: KDS,
  'kitchen-tickets': TicketIcon,
  tables: TablesIcon,
  blueprints: MapIcon,
  other: Other,
  safT: ReportIcon,
  ejournal: ReportIcon,
  'staff-settings': StaffIcon,
  app: AppIcon,
};

interface Props {
  page: PageButtonType;
  /** Must be a stable reference so the memoized button doesn't re-render on parent updates */
  setSelectedPage: (page: PageButtonType) => void;
  /** Renders a full-width list row instead of the square tile */
  isHandy?: boolean;
}

const PageButton: FC<Props> = ({ page, setSelectedPage, isHandy }) => {
  const { i18n } = useI18n();

  const button = useButton({
    onClick: () => setSelectedPage(page),
  });

  const BUTTON_TITLES = useMemo<Record<PageType, string>>(
    () => ({
      printers: i18n('Settings.Printers'),
      layout: i18n('Settings.PosLayout'),
      payments: i18n('Settings.Payments'),
      receipts: i18n('Settings.Receipts'),
      KDS: i18n('Settings.KdsSettings'),
      'kitchen-tickets': i18n('Settings.KitchenTickets'),
      tables: i18n('Settings.Tables'),
      blueprints: i18n('Settings.Blueprints'),
      safT: 'Saf-T',
      ejournal: i18n('Settings.EJournal'),
      other: i18n('Settings.Other'),
      developer: 'Developer',
      'staff-settings': i18n('Settings.StaffSettings'),
      app: i18n('Settings.AppSettings'),
    }),
    [i18n],
  );

  if (isHandy) {
    return (
      <Row {...button}>
        <Box display="flex" alignItems="center">
          <RoundedIcon bgColor={PAGE_COLORS[page]}>
            <Icon src={ICONS[page]} height="22px" width="22px" color="var(--colorBlueGray1100)" />
          </RoundedIcon>
          <Heading level={4} color="var(--colorBlueGray50)">
            {BUTTON_TITLES[page]}
          </Heading>
        </Box>
        <Icon src={ForwardArrow} height="24px" width="24px" color="var(--colorBlueGray100)" />
      </Row>
    );
  }

  return (
    <SettingsButton key={page} page={page} {...button}>
      <IconWrapper>
        <Icon src={ICONS[page]} height="58px" width="48px" color={PAGE_COLORS[page]} />
      </IconWrapper>
      <Heading level={4} bottom={4} color="var(--colorWhite)">
        {BUTTON_TITLES[page]}
      </Heading>
    </SettingsButton>
  );
};

export default memo(PageButton);
